import { createFileRoute } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Money } from '../../../../components/ui/Money';
import { useTrialBalance } from '../../../../features/reports/client';
import { useState } from 'react';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/FormField';
import { Skeleton } from '../../../../components/ui/Skeleton';

export const Route = createFileRoute('/_auth/reports/financial/trial-balance')({
  component: TrialBalancePage,
});

function TrialBalancePage() {
  const [asOfDate, setAsOfDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );
  const { data, isLoading, isError, refetch } = useTrialBalance(asOfDate || undefined);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Trial Balance"
        subtitle="Financial statement showing the balances of all general ledger accounts as of a specific date."
        action={null}
      />

      <div className="flex gap-4 items-center mb-6">
        <label className="text-sm text-text-secondary">As of date</label>
        <Input
          type="date"
          className="w-auto"
          value={asOfDate}
          onChange={(e) => setAsOfDate(e.target.value)}
        />
      </div>

{!asOfDate ? (
        <div className="p-8 text-center">
          <p className="text-sm text-text-muted">Select a date to view the trial balance.</p>
        </div>
      ) : isLoading ? (
        <Skeleton rows={8} />
      ) : isError ? (
        <div className="p-8 text-center">
          <p className="text-sm font-semibold text-danger-600">Failed to load trial balance</p>
          <Button variant="secondary" size="sm" className="mt-3" onClick={() => refetch()}>Retry</Button>
        </div>
      ) : data ? (
        <>
          {!data.isBalanced && (
            <div className="bg-danger-bg border border-danger-bg text-danger-text px-4 py-3 rounded mb-6">
              <strong>Alert:</strong> Trial balance is not balanced! Total Debit does not equal Total Credit.
            </div>
          )}

          <div className="bg-surface-1 border border-border rounded-xl overflow-hidden shadow-sm">
            <table className="w-full border-collapse text-left text-sm">
              <thead className="bg-surface-2 text-text-secondary font-semibold uppercase tracking-wider text-xs border-b border-border">
                <tr>
                  <th className="p-4">Account Code</th>
                  <th className="p-4">Account Name</th>
                  <th className="p-4 text-right">Debit (₹)</th>
                  <th className="p-4 text-right">Credit (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-primary">
                {data.rows?.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="p-8 text-center text-text-muted">
                      <span className="font-semibold">No ledger entries found</span>
                    </td>
                  </tr>
                ) : (
                  data.rows?.map((entry: any) => (
                    <tr key={entry.accountCode} className="hover:bg-surface-2 transition-colors">
                      <td className="p-4 font-mono text-xs text-text-muted">{entry.accountCode}</td>
                      <td className="p-4 font-semibold">{entry.accountName}</td>
                      <td className="p-4 text-right font-mono">
                        {entry.drBalance > 0 ? <Money paise={entry.drBalance} /> : <span className="text-text-muted">—</span>}
                      </td>
                      <td className="p-4 text-right font-mono">
                        {entry.crBalance > 0 ? <Money paise={entry.crBalance} /> : <span className="text-text-muted">—</span>}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className="bg-surface-2 font-bold border-t-2 border-border text-sm">
                <tr>
                  <td colSpan={2} className="p-4 text-right">Total</td>
                  <td className="p-4 text-right font-mono"><Money paise={data.totalDr || 0} /></td>
                  <td className="p-4 text-right font-mono"><Money paise={data.totalCr || 0} /></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : null}
    </div>
  );
}
